import { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faLinkedin,
  faFacebook,
  faInstagram,
  faXTwitter,
  faYoutube,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
  faBluesky,
} from '@fortawesome/free-brands-svg-icons';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { ICONS } from '@/constants/icons';
import { DashboardSection } from './DashboardSection';
import { useWeeklyGoalProgress, useDeleteWeeklyGoal } from '@/hooks/useDashboardStats';
import { useChannels } from '@/hooks/useChannels';
import { Channel } from '@/services/channels.service';
import { isSocialPublishTarget } from '@/constants/platforms';
import { EditGoalsModal } from './EditGoalsModal';
import { SectionTitle } from './SectionTitle';
import { ConfirmDialogSimple } from '@/components/ui/ConfirmDialog';
import { useTimezone } from '@/contexts/TimezoneContext';
import { useAuth } from '@/hooks/useAuth';
import { getChannelAvatar } from '@/utils/channel-avatar';

/**
 * Weekly Goals Section - Goal tracking and progress
 * Shows weekly goals with progress indicators in two side-by-side panels:
 * "My Goals" (personal) and "Team Goals".
 */

// Platform icon mapping for FontAwesome
const PLATFORM_FA_ICONS: Record<string, IconDefinition> = {
  LINKEDIN: faLinkedin,
  FACEBOOK: faFacebook,
  INSTAGRAM: faInstagram,
  TWITTER: faXTwitter,
  YOUTUBE: faYoutube,
  TIKTOK: faTiktok,
  PINTEREST: faPinterest,
  THREADS: faThreads,
  GOOGLE_MY_BUSINESS: faGoogle,
  BLUESKY: faBluesky,
};

// Platform colors for icons
const PLATFORM_COLORS: Record<string, string> = {
  LINKEDIN: '#0A66C2',
  FACEBOOK: '#1877F2',
  INSTAGRAM: '#E4405F',
  TWITTER: '#000000',
  YOUTUBE: '#FF0000',
  TIKTOK: '#000000',
  PINTEREST: '#E60023',
  THREADS: '#000000',
  GOOGLE_MY_BUSINESS: '#4285F4',
  BLUESKY: '#0085FF',
};

// Platform labels
const PLATFORM_LABELS: Record<string, string> = {
  LINKEDIN: 'LinkedIn',
  FACEBOOK: 'Facebook',
  INSTAGRAM: 'Instagram',
  TWITTER: 'X (Twitter)',
  YOUTUBE: 'YouTube',
  TIKTOK: 'TikTok',
  PINTEREST: 'Pinterest',
  THREADS: 'Threads',
  GOOGLE_MY_BUSINESS: 'Google Business',
  BLUESKY: 'Bluesky',
};

const getProgress = (current: number, target: number) => Math.min((current / target) * 100, 100);

// ─── GoalPanel ────────────────────────────────────────────────────────────────

interface GoalPanelProps {
  scope: 'team' | 'personal';
  title: string;
  userId: number | null;
  channels: Channel[];
  onDeleteGoal: (id: number) => void;
}

const GoalPanel = ({ scope, userId, channels, onDeleteGoal }: GoalPanelProps) => {
  const { t } = useTranslation('dashboard');
  const [selectedChannel, setSelectedChannel] = useState<number | null>(null);
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [channelSearch, setChannelSearch] = useState('');
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Fetch goal progress for this panel's scope
  const { data: progressData, isLoading } = useWeeklyGoalProgress(
    selectedChannel,
    userId,
    selectedPlatform
  );

  // Calculate dropdown position when it opens; reset search when it closes
  useEffect(() => {
    if (isDropdownOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownWidth = 256; // w-64
      const maxLeft = window.innerWidth - dropdownWidth - 8;
      setDropdownPos({ top: rect.bottom + 4, left: Math.max(8, Math.min(rect.left, maxLeft)) });
    } else {
      setChannelSearch('');
    }
  }, [isDropdownOpen]);

  // Filter channels by search term
  const filteredChannels = useMemo(() => {
    if (!channelSearch.trim()) return channels;
    const q = channelSearch.toLowerCase();
    return channels.filter(
      (c) =>
        (c.platformUsername || '').toLowerCase().includes(q) ||
        (PLATFORM_LABELS[c.platform] || c.platform).toLowerCase().includes(q)
    );
  }, [channels, channelSearch]);

  // Current channel display data
  const currentChannelData = channels.find((c) => c.id === selectedChannel);
  const currentChannelAvatar = currentChannelData
    ? getChannelAvatar(currentChannelData.metadata as Record<string, unknown> | undefined)
    : null;

  let displayIcon: IconDefinition = faGlobe;
  let displayColor: string = '#6B7280';
  let displayName: string = t('dashboard_goals_all_channels', { defaultValue: 'All Channels' });

  if (selectedChannel && currentChannelData) {
    displayIcon = PLATFORM_FA_ICONS[currentChannelData.platform] || faGlobe;
    displayColor = PLATFORM_COLORS[currentChannelData.platform] || '#6B7280';
    displayName = currentChannelData.platformUsername || currentChannelData.platform;
  } else if (selectedPlatform) {
    displayIcon = PLATFORM_FA_ICONS[selectedPlatform] || faGlobe;
    displayColor = PLATFORM_COLORS[selectedPlatform] || '#6B7280';
    displayName = PLATFORM_LABELS[selectedPlatform] || selectedPlatform;
  }

  const hasGoals = progressData && progressData.goals.length > 0;
  const goals = progressData?.goals ?? [];
  const streak = progressData?.streak ?? 0;

  return (
    <div className="flex flex-col min-h-0">
      {/* Panel toolbar: streak badge + channel selector */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          {streak > 0 && (
            <Tooltip
              content={t('dashboard_goals_streak_tooltip', {
                defaultValue: 'Consecutive weeks with all goals completed',
              })}
            >
              <div className="flex items-center gap-1 px-1.5 py-0.5 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100 rounded-full cursor-default">
                <span className="text-xs">🔥</span>
                <span className="text-[10px] font-semibold text-amber-700">
                  {streak}
                </span>
              </div>
            </Tooltip>
          )}
        </div>

        {/* Channel selector */}
        <Tooltip
          content={t('dashboard_goals_channel_tooltip', { defaultValue: 'Filter by channel or platform' })}
          disabled={isDropdownOpen}
        >
          <button
            ref={triggerRef}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors min-w-0 max-w-[160px]"
          >
            {selectedChannel && currentChannelAvatar ? (
              <div className="relative flex-shrink-0">
                <img src={currentChannelAvatar} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />
                <span
                  className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full flex items-center justify-center border border-white"
                  style={{ backgroundColor: displayColor }}
                >
                  <FontAwesomeIcon icon={displayIcon} className="text-white text-[4px]" />
                </span>
              </div>
            ) : (
              <FontAwesomeIcon
                icon={displayIcon}
                className="w-3 h-3 flex-shrink-0"
                style={{ color: displayColor }}
                aria-hidden="true"
              />
            )}
            <span className="truncate">{displayName}</span>
            <Icon name={ICONS.ANGLE_DOWN} size="xs" color="text-gray-400" />
          </button>
        </Tooltip>

        {isDropdownOpen &&
          createPortal(
            <>
              <div
                className="fixed inset-0 z-[60]"
                onClick={() => setIsDropdownOpen(false)}
              />
              <div
                className="fixed w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-[61] max-h-96 flex flex-col"
                style={{ top: dropdownPos.top, left: dropdownPos.left }}
              >
                {/* Search input */}
                <div className="p-2 border-b border-gray-100 flex-shrink-0">
                  <input
                    type="text"
                    value={channelSearch}
                    onChange={(e) => setChannelSearch(e.target.value)}
                    placeholder={t('dashboard_goals_search_channels', { defaultValue: 'Search channels...' })}
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    autoFocus
                  />
                </div>

                <div className="overflow-y-auto py-1">
                  {/* All Channels option */}
                  {!channelSearch && (
                    <button
                      onClick={() => {
                        setSelectedChannel(null);
                        setSelectedPlatform(null);
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${
                        selectedChannel === null && selectedPlatform === null
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-700'
                      }`}
                    >
                      <Icon
                        name={ICONS.GRID}
                        size="sm"
                        color={
                          selectedChannel === null && selectedPlatform === null
                            ? 'text-primary-600'
                            : 'text-gray-400'
                        }
                      />
                      <span>{t('dashboard_goals_all_channels', { defaultValue: 'All Channels' })}</span>
                      {selectedChannel === null && selectedPlatform === null && (
                        <Icon name={ICONS.CHECK} size="sm" color="text-primary-600" className="ml-auto" />
                      )}
                    </button>
                  )}

                  {/* Platform groups */}
                  {Array.from(
                    filteredChannels.reduce((acc, ch) => {
                      const list = acc.get(ch.platform) || [];
                      list.push(ch);
                      acc.set(ch.platform, list);
                      return acc;
                    }, new Map<string, Channel[]>())
                  ).map(([platform, platformChannels]) => (
                    <div key={platform}>
                      {/* Platform header - clickable for platform-level filter */}
                      <button
                        onClick={() => {
                          setSelectedChannel(null);
                          setSelectedPlatform(platform);
                          setIsDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${
                          selectedChannel === null && selectedPlatform === platform
                            ? 'bg-primary-50 text-primary-700'
                            : 'text-gray-700'
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={PLATFORM_FA_ICONS[platform] || faGlobe}
                          className="w-3.5 h-3.5"
                          style={{ color: PLATFORM_COLORS[platform] || '#6B7280' }}
                          aria-hidden="true"
                        />
                        <span className="font-medium">{PLATFORM_LABELS[platform] || platform}</span>
                        <span className="text-xs text-gray-400 ml-1">({platformChannels.length})</span>
                        {selectedChannel === null && selectedPlatform === platform && (
                          <Icon name={ICONS.CHECK} size="sm" color="text-primary-600" className="ml-auto" />
                        )}
                      </button>

                      {/* Individual channels under this platform */}
                      {platformChannels.map((channel) => {
                        const isSelected = selectedChannel === channel.id && selectedPlatform === null;
                        const avatarUrl = getChannelAvatar(
                          channel.metadata as Record<string, unknown> | undefined
                        );
                        return (
                          <button
                            key={channel.id}
                            onClick={() => {
                              setSelectedChannel(channel.id);
                              setSelectedPlatform(null);
                              setIsDropdownOpen(false);
                            }}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 pl-7 text-sm text-left hover:bg-gray-50 transition-colors ${
                              isSelected ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                            }`}
                          >
                            {/* Avatar with platform badge */}
                            <div className="relative flex-shrink-0">
                              {avatarUrl ? (
                                <>
                                  <img src={avatarUrl} alt="" className="w-6 h-6 rounded-full object-cover" />
                                  <span
                                    className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center border-[1.5px] border-white"
                                    style={{
                                      backgroundColor: PLATFORM_COLORS[channel.platform] || '#6B7280',
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={PLATFORM_FA_ICONS[channel.platform] || faGlobe}
                                      className="text-white text-[6px]"
                                    />
                                  </span>
                                </>
                              ) : (
                                <span
                                  className="w-6 h-6 rounded-full flex items-center justify-center"
                                  style={{
                                    backgroundColor: PLATFORM_COLORS[channel.platform] || '#6B7280',
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={PLATFORM_FA_ICONS[channel.platform] || faGlobe}
                                    className="text-white text-[10px]"
                                  />
                                </span>
                              )}
                            </div>
                            <span className="truncate flex-1">
                              {channel.platformUsername || channel.platform}
                            </span>
                            {isSelected && (
                              <Icon
                                name={ICONS.CHECK}
                                size="sm"
                                color="text-primary-600"
                                className="ml-auto flex-shrink-0"
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ))}

                  {/* No results */}
                  {channelSearch && filteredChannels.length === 0 && (
                    <div className="px-3 py-4 text-sm text-gray-400 text-center">
                      {t('dashboard_goals_no_channels_found', { defaultValue: 'No channels found' })}
                    </div>
                  )}
                </div>
              </div>
            </>,
            document.body
          )}
      </div>

      {/* Body: Loading | Empty | Goals */}
      {isLoading ? (
        <div className="space-y-4 flex-1">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-3.5 bg-gray-200 rounded w-28 animate-pulse" />
              <div className="h-2 bg-gray-200 rounded-full animate-pulse" />
            </div>
          ))}
        </div>
      ) : !hasGoals ? (
        <div className="text-center py-5 flex-1 flex flex-col items-center justify-center">
          <div className="w-11 h-11 bg-primary-100 rounded-full grid place-items-center mx-auto mb-3">
            <Icon name={ICONS.TARGET} size="md" color="text-primary-600" />
          </div>
          <h4 className="text-sm font-semibold text-gray-900 mb-1.5">
            {scope === 'personal'
              ? t('dashboard_goals_empty_personal_title', { defaultValue: 'No personal goals yet' })
              : t('dashboard_goals_empty_title', { defaultValue: 'Set your first weekly goals' })}
          </h4>
          <p className="text-xs text-gray-500 mb-4 max-w-[180px]">
            {scope === 'personal'
              ? t('dashboard_goals_empty_personal_description', {
                  defaultValue: 'Create goals to track your personal progress',
                })
              : t('dashboard_goals_empty_description', {
                  defaultValue: 'Track your progress and stay motivated',
                })}
          </p>
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="px-4 py-2 bg-primary-500 text-primary-contrast text-xs font-semibold rounded-lg hover:bg-primary-600 transition-colors shadow-sm"
          >
            {t('dashboard_goals_create', { defaultValue: 'Create Goals' })}
          </button>
        </div>
      ) : (
        <div className="flex flex-col flex-1">
          <div className="space-y-4 flex-1">
            {goals.map((goal) => {
              const progress = getProgress(goal.currentValue, goal.targetValue);
              const isComplete = progress >= 100;
              const isAlmostDone = progress >= 80;

              return (
                <div key={goal.id} className="group">
                  {/* Label + Values + Delete */}
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {(() => {
                        const goalChannel = goal.channelId
                          ? channels.find((c) => c.id === goal.channelId)
                          : null;
                        const avatarUrl = goalChannel
                          ? getChannelAvatar(goalChannel.metadata as Record<string, unknown> | undefined)
                          : null;
                        const platform = goal.platform || goalChannel?.platform;

                        if (avatarUrl && platform) {
                          return (
                            <div className="relative flex-shrink-0">
                              <img src={avatarUrl} alt="" className="w-4 h-4 rounded-full object-cover" />
                              <span
                                className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full flex items-center justify-center border border-white"
                                style={{ backgroundColor: PLATFORM_COLORS[platform] || '#6B7280' }}
                              >
                                <FontAwesomeIcon
                                  icon={PLATFORM_FA_ICONS[platform] || faGlobe}
                                  className="text-white text-[4px]"
                                />
                              </span>
                            </div>
                          );
                        }

                        if (platform) {
                          return (
                            <FontAwesomeIcon
                              icon={PLATFORM_FA_ICONS[platform] || faGlobe}
                              className="w-3 h-3 flex-shrink-0"
                              style={{ color: PLATFORM_COLORS[platform] || '#6B7280' }}
                              aria-hidden="true"
                            />
                          );
                        }

                        return null;
                      })()}
                      <span className="text-xs font-medium text-gray-700 truncate">
                        {t(`goal_${goal.goalType.toLowerCase()}`, { defaultValue: goal.label })}
                        {goal.channelName && (
                          <span className="text-gray-400 font-normal ml-1">· {goal.channelName}</span>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      {/* Delete button - visible on hover */}
                      <Tooltip
                        content={t('dashboard_goals_delete', { defaultValue: 'Delete goal' })}
                        side="left"
                      >
                        <button
                          type="button"
                          onClick={() => onDeleteGoal(goal.id)}
                          aria-label={t('dashboard_goals_delete', { defaultValue: 'Delete goal' })}
                          className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100 h-5 w-5 [@media(hover:none)]:min-h-[44px] [@media(hover:none)]:min-w-[44px] flex items-center justify-center rounded-md text-gray-500 hover:text-red-600 focus-visible:text-red-600 focus-visible:bg-gray-100 transition-colors flex-shrink-0"
                        >
                          <Icon name={ICONS.RECYCLE_BIN} size="sm" ariaHidden />
                        </button>
                      </Tooltip>
                      <span
                        className={`text-xs font-bold ${isComplete ? 'text-primary-600' : 'text-gray-900'}`}
                      >
                        {goal.currentValue.toLocaleString()}
                        {goal.unit ? ` ${goal.unit}` : ''}
                        <span className="text-gray-400 font-normal">
                          {' '}/ {goal.targetValue.toLocaleString()}
                          {goal.unit ? ` ${goal.unit}` : ''}
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <Tooltip content={`${Math.round(progress)}%`} side="bottom" delayDuration={100}>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden cursor-default">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          isComplete
                            ? 'bg-gradient-to-r from-primary-400 to-primary-500'
                            : isAlmostDone
                              ? 'bg-gradient-to-r from-primary-300 to-primary-400'
                              : 'bg-primary-300'
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </Tooltip>
                </div>
              );
            })}
          </div>

          {/* Edit goals button */}
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="w-full mt-4 px-3 py-2 bg-white border border-gray-200 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors flex items-center justify-center gap-1.5"
          >
            <Icon name={ICONS.EDIT} size="xs" color="text-gray-500" />
            {t('dashboard_goals_edit', { defaultValue: 'Edit Weekly Goals' })}
          </button>
        </div>
      )}

      <EditGoalsModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        channelId={selectedChannel}
        initialScope={scope}
      />
    </div>
  );
};

// ─── WeeklyGoalsSection ───────────────────────────────────────────────────────

export const WeeklyGoalsSection = () => {
  const { t } = useTranslation('dashboard');
  const { formatDateOnly } = useTimezone();
  const { user } = useAuth();
  const [deleteConfirmGoalId, setDeleteConfirmGoalId] = useState<number | null>(null);
  const [personalCollapsed, setPersonalCollapsed] = useState(false);
  const [teamCollapsed, setTeamCollapsed] = useState(false);

  // Fetch channels once at section level; pass down to both panels
  const { data: channelsData } = useChannels();
  const channels = useMemo(() => {
    return (
      channelsData?.filter(
        (c: Channel) => c.isActive && isSocialPublishTarget(c.platform) && c.platform !== 'TWITTER'
      ) ?? []
    );
  }, [channelsData]);

  // Delete goal mutation (shared between panels)
  const deleteGoal = useDeleteWeeklyGoal();

  // Fetch team-scope progress solely for the shared week range indicator
  const { data: weekData } = useWeeklyGoalProgress(null, null, null);
  const weekStart = weekData?.weekStart;
  const weekEnd = weekData?.weekEnd;

  const formatWeekRange = (start: string, end: string): string =>
    `${formatDateOnly(start)} - ${formatDateOnly(end)}`;

  const handleDeleteGoal = (id: number) => setDeleteConfirmGoalId(id);

  return (
    <>
      {/* Section title with week dates as subtitle */}
      <div className="flex items-baseline gap-3 mb-4">
        <SectionTitle title={t('dashboard_section_goals', { defaultValue: 'Weekly Goals' })} />
        {weekStart && weekEnd && (
          <Tooltip
            content={t('dashboard_goals_week_tooltip', {
              defaultValue: 'Current tracking week (Mon–Sun)',
            })}
          >
            <span className="text-xs text-gray-500 cursor-default">
              {formatWeekRange(weekStart, weekEnd)}
            </span>
          </Tooltip>
        )}
      </div>

      {/* Side-by-side panels, each collapses independently */}
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${personalCollapsed || teamCollapsed ? 'items-start' : ''}`}>
        <DashboardSection
          id="goals-personal"
          title={t('dashboard_goals_my_goals', { defaultValue: 'My Goals' })}
          icon={ICONS.USER}
          collapsed={personalCollapsed}
          onToggleCollapse={() => setPersonalCollapsed((v) => !v)}
        >
          <GoalPanel
            scope="personal"
            title={t('dashboard_goals_my_goals', { defaultValue: 'My Goals' })}
            userId={user?.id ?? null}
            channels={channels}
            onDeleteGoal={handleDeleteGoal}
          />
        </DashboardSection>
        <DashboardSection
          id="goals-team"
          title={t('dashboard_goals_team_goals', { defaultValue: 'Team Goals' })}
          icon={ICONS.TEAM}
          collapsed={teamCollapsed}
          onToggleCollapse={() => setTeamCollapsed((v) => !v)}
        >
          <GoalPanel
            scope="team"
            title={t('dashboard_goals_team_goals', { defaultValue: 'Team Goals' })}
            userId={null}
            channels={channels}
            onDeleteGoal={handleDeleteGoal}
          />
        </DashboardSection>
      </div>

      {/* Shared delete confirmation dialog */}
      <ConfirmDialogSimple
        open={deleteConfirmGoalId !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirmGoalId(null);
        }}
        title={t('dashboard_goals_delete_title', { defaultValue: 'Delete this goal?' })}
        description={t('dashboard_goals_delete_description', {
          defaultValue: 'This will permanently remove this goal and its history.',
        })}
        confirmText={t('dashboard_goals_delete_confirm', { defaultValue: 'Delete' })}
        cancelText={t('common:cancel', { defaultValue: 'Cancel' })}
        variant="danger"
        onConfirm={() => {
          if (deleteConfirmGoalId !== null) {
            deleteGoal.mutate(deleteConfirmGoalId);
            setDeleteConfirmGoalId(null);
          }
        }}
      />
    </>
  );
};
